import { useState, useEffect, useMemo } from "react";
import {
  Group,
  Title,
  Text,
  Stack,
  Box,
  Button,
  Divider,
  Drawer,
  ActionIcon,
  ScrollArea,
  Card,
  Checkbox,
} from "@mantine/core";
import { L2KBadge } from "../components/ui/Typography";
import { useDisclosure } from "@mantine/hooks";
import {
  IconExternalLink,
  IconUsers,
  IconClock,
  IconWorld,
  IconMusic,
  IconUser,
  IconMapPin,
  IconUmbrellaFilled,
  IconStretching2,
} from "@tabler/icons-react";
import HybridLayout from "../components/layouts/HybridLayout";
import PageContainer from "../components/layouts/PageContainer";
import ModernPagination from "../components/ui/Pagination";
import { L2KH2 } from "../components/ui/Typography";
import { SearchFilterBar } from "../components/common/SearchFilterBar";

// Import data from separate files
import { danceStyles } from "../data/danceStylesData";
import { danceStyleDictionaryEntries } from "../data/danceStyleDictionaryEntries";

// Keep hardcoded for now (as requested)
const categories = [
  "Culture",
  "Dance",
  "Music",
  "History",
  "Person",
  "Place",
  "Umbrella",
];

// Category icon mapping
const getCategoryIcon = (category: string) => {
  const iconMap = {
    Dance: IconStretching2,
    History: IconClock,
    Culture: IconWorld,
    Music: IconMusic,
    Person: IconUser,
    Place: IconMapPin,
    Umbrella: IconUmbrellaFilled,
  };
  return iconMap[category as keyof typeof iconMap] || IconUsers;
};

// For entries that might have multiple categories, get all applicable icons
const getCategoryIcons = (term: (typeof dictionaryTerms)[number]) => {
  const categories = Array.isArray(term.category)
    ? term.category
    : [term.category];

  // Sort categories to show Umbrella first, then others in original order
  const sortedCategories = [...categories].sort((a, b) => {
    if (a === "Umbrella") return -1;
    if (b === "Umbrella") return 1;
    return 0;
  });

  return sortedCategories.map((category) => getCategoryIcon(category));
};

const multimediaReferences = [
  {
    id: 1,
    type: "internal_video",
    title: "Basic Step Tutorial",
    url: "/videos/basic-step",
  },
  {
    id: 2,
    type: "internal_image",
    title: "Hand Position Guide",
    url: "/images/hand-position.jpg",
  },
  {
    id: 3,
    type: "external_video",
    title: "YouTube: Connection Basics",
    url: "https://youtube.com/watch?v=example",
  },
  {
    id: 4,
    type: "external_link",
    title: "History of Kizomba",
    url: "https://example.com/kizomba-history",
  },
  {
    id: 5,
    type: "podcast",
    title: "Dance Culture Podcast Ep. 12",
    url: "https://example.com/podcast",
  },
];

// Use imported data
const dictionaryTerms = danceStyleDictionaryEntries;

export default function DanceDictionary() {
  const [filterDrawerOpened, { toggle: toggleFilterDrawer }] = useDisclosure();
  const [termDrawerOpened, { toggle: toggleTermDrawer }] = useDisclosure();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [selectedDanceStyles, setSelectedDanceStyles] = useState<number[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  type DictionaryTerm = (typeof dictionaryTerms)[number];
  const [selectedTerm, setSelectedTerm] = useState<DictionaryTerm | null>(null);
  const [debouncingSearch, setDebouncingSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  // Handle search debouncing
  useEffect(() => {
    setDebouncingSearch(true);
    const timer = setTimeout(() => {
      setDebouncingSearch(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Advanced search and filtering
  const filteredTerms = useMemo(() => {
    let filtered = dictionaryTerms;

    // Text search with partial matching and typo tolerance
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((term) => {
        // Search in name, definition, and see also terms
        const searchIn = [
          term.name.toLowerCase(),
          term.definition.toLowerCase(),
          ...term.seeAlso.map(
            (id) =>
              dictionaryTerms.find((t) => t.id === id)?.name.toLowerCase() || ""
          ),
        ].join(" ");

        return (
          searchIn.includes(search) ||
          // Simple typo tolerance - allow one character difference
          term.name
            .toLowerCase()
            .replace(/[aeiou]/g, "")
            .includes(search.replace(/[aeiou]/g, ""))
        );
      });
    }

    // Letter filter
    if (selectedLetter) {
      filtered = filtered.filter((term) =>
        term.name.toUpperCase().startsWith(selectedLetter)
      );
    }

    // Dance style filter
    if (selectedDanceStyles.length > 0) {
      filtered = filtered.filter((term) =>
        term.danceStyles.some((styleId) =>
          selectedDanceStyles.includes(styleId)
        )
      );
    }

    // Category filter
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((term) => {
        const termCategories = Array.isArray(term.category)
          ? term.category
          : [term.category];
        return termCategories.some((cat) => selectedCategories.includes(cat));
      });
    }

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [searchTerm, selectedLetter, selectedDanceStyles, selectedCategories]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedLetter, selectedDanceStyles, selectedCategories]);

  // Paginated terms
  const paginatedTerms = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredTerms.slice(startIndex, endIndex);
  }, [filteredTerms, currentPage]);

  const totalPages = Math.ceil(filteredTerms.length / ITEMS_PER_PAGE);

  const activeFilterCount =
    selectedDanceStyles.length +
    selectedCategories.length +
    (selectedLetter ? 1 : 0);

  const getDanceStyleName = (id: number) =>
    danceStyles.find((style) => style.id === id)?.name || "";
  const getUmbrellaStyles = () =>
    danceStyles.filter((style) => style.isUmbrella);
  const getSubStyles = (parentId: number) =>
    danceStyles.filter((style) => style.parent === parentId);
  const getStandaloneStyles = () =>
    danceStyles.filter((style) => !style.isUmbrella && !style.parent);

  const handleTermClick = (term: DictionaryTerm) => {
    setSelectedTerm(term);
    toggleTermDrawer();
  };

  const getWordSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  };

  const handleDanceStyleToggle = (styleId: number) => {
    setSelectedDanceStyles((prev) =>
      prev.includes(styleId)
        ? prev.filter((id) => id !== styleId)
        : [...prev, styleId]
    );
  };

  const handleDanceStyleBadgeClick = (
    styleId: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();

    // Find dictionary entry where danceStyles array CONTAINS the clicked style ID
    const danceStyleEntry = dictionaryTerms.find((entry) =>
      entry.danceStyles.includes(styleId)
    );

    if (danceStyleEntry) {
      setSelectedTerm(danceStyleEntry);
      toggleTermDrawer();
    }
  };

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedLetter("");
    setSelectedDanceStyles([]);
    setSelectedCategories([]);
    setCurrentPage(1);
    toggleFilterDrawer();
  };

  // App shell styles using urbankiz theme
  const appShellStyles = {
    background: `
      radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
      radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
      radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
      linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
    `,
    minHeight: "100vh",
    fontFamily: "'Space Grotesk', sans-serif",
    color: "#F0F0F0",
    marginTop: "0px",
  };

  return (
    <Box style={appShellStyles}>
      <PageContainer>
        <Stack gap="8px" py="sm">
        {/* Header */}
        <L2KH2 variant="accent" ta="center" mb="0px">
          Dance Dictionary
        </L2KH2>

        {/* Search and Filter Bar */}
        <SearchFilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search terms, definitions, or related concepts..."
          onFilterClick={toggleFilterDrawer}
          activeFilterCount={activeFilterCount}
          debouncing={debouncingSearch}
          variant="compact"
        />

        {/* Pagination - Top */}
        {totalPages > 1 && (
          <ModernPagination
            total={totalPages}
            value={currentPage}
            onChange={setCurrentPage}
            totalItems={filteredTerms.length}
            itemsPerPage={ITEMS_PER_PAGE}
            showItemsInfo={false}
            size="md"
            style={{ paddingTop: 0 }}
          />
        )}

        {/* Results Summary */}
        <Group justify="space-between" align="center">
          <Text
            size="sm"
            c="#B8B8B8"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Showing {paginatedTerms.length} of {filteredTerms.length} words
            {searchTerm && ` for "${searchTerm}"`}
            {selectedLetter && ` starting with "${selectedLetter}"`}
          </Text>

          {totalPages > 1 && (
            <Text
              size="sm"
              c="#B8B8B8"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Page {currentPage} of {totalPages}
            </Text>
          )}
        </Group>

        {/* Dictionary Terms List */}
        <Stack gap="sm">
          {paginatedTerms.map((term) => {
            const categoryIcons = getCategoryIcons(term);
            return (
              <Card
                key={term.id}
                padding="lg"
                radius="md"
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.borderColor = "rgba(255, 152, 0, 0.5)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 25px rgba(255, 152, 0, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.borderColor =
                    "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                onClick={() => handleTermClick(term)}
              >
                {/* Category Icons in upper right */}
                <Box
                  style={{
                    position: "absolute",
                    top: "16px",
                    right: "16px",
                    color: "#FF9800",
                    opacity: 0.7,
                    display: "flex",
                    gap: "4px",
                  }}
                >
                  {categoryIcons.map((IconComponent, index) => (
                    <IconComponent key={index} size={20} />
                  ))}
                </Box>

                <Group
                  justify="space-between"
                  align="flex-start"
                  style={{ paddingRight: "60px" }}
                >
                  <Stack gap="sm" style={{ flex: 1 }}>
                    <Group gap="sm">
                      <Title
                        order={3}
                        style={{
                          fontFamily: "'Big Shoulders Display', sans-serif",
                          color: "#F0F0F0",
                          fontWeight: 700,
                          fontSize: "1.2rem",
                        }}
                      >
                        {term.name}
                      </Title>
                    </Group>
                    <Text
                      size="sm"
                      c="#D0D0D0"
                      lineClamp={2}
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      {term.definition}
                    </Text>
                    {/* Dance style badges moved under definition */}
                    <Group gap="8px">
                      {term.danceStyles.map((styleId) => (
                        <L2KBadge
                          key={styleId}
                          variant="subtle"
                          size="sm"
                          style={{
                            fontFamily: "'Montesserat', sans-serif",
                            fontSize: "0.65rem",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                          }}
                          onClick={(event) =>
                            handleDanceStyleBadgeClick(styleId, event)
                          }
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(255, 152, 0, 0.3)";
                            e.currentTarget.style.transform =
                              "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "rgba(255, 152, 0, 0.1)";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          {getDanceStyleName(styleId)}
                        </L2KBadge>
                      ))}
                    </Group>
                  </Stack>
                </Group>
              </Card>
            );
          })}
        </Stack>

        {/* Pagination - Bottom */}
        {totalPages > 1 && (
          <ModernPagination
            total={totalPages}
            value={currentPage}
            onChange={setCurrentPage}
            totalItems={filteredTerms.length}
            itemsPerPage={ITEMS_PER_PAGE}
            showItemsInfo={true}
            size="md"
          />
        )}

        {filteredTerms.length === 0 && (
          <Box ta="center" py="xl">
            <Title
              order={3}
              c="#B8B8B8"
              mb="md"
              style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600 }}
            >
              No terms found
            </Title>
            <Text
              c="#B8B8B8"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Try adjusting your search or filters
            </Text>
          </Box>
        )}
        </Stack>
      </PageContainer>

      {/* Filter Drawer */}
      <Drawer
        opened={filterDrawerOpened}
        onClose={toggleFilterDrawer}
        title={
          <Title
            order={3}
            style={{ fontFamily: "'Big Shoulders Display', sans-serif", color: "#F0F0F0" }}
          >
            Filter Dictionary
          </Title>
        }
        position="right"
        size="md"
        styles={{
          content: {
            background: "rgba(23, 23, 23, 0.95)",
            backdropFilter: "blur(15px)",
            border: "1px solid rgba(255, 152, 0, 0.3)",
          },
          header: {
            backgroundColor: "transparent",
            borderBottom: "1px solid rgba(255, 152, 0, 0.3)",
          },
          title: {
            fontFamily: "'Big Shoulders Display', sans-serif",
            color: "#F0F0F0",
          },
        }}
      >
        {/* Scrollable Content */}
        <ScrollArea
          style={{ height: "calc(100vh - 150px)", paddingBottom: "0px" }}
        >
          <Stack gap="lg" p="md">
            {/* Dance Styles Section */}
            <Box>
              <Title
                order={4}
                c="#F0F0F0"
                mb="md"
                style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600 }}
              >
                Dance Styles
              </Title>

              {getUmbrellaStyles().map((umbrella) => (
                <Box key={umbrella.id} mb="lg">
                  <Text
                    size="md"
                    fw={600}
                    c="#FF9800"
                    mb="xs"
                    style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                  >
                    {umbrella.name}
                  </Text>
                  <Stack gap="xs" ml="md">
                    <Checkbox
                      label={umbrella.name}
                      checked={selectedDanceStyles.includes(umbrella.id)}
                      onChange={() => handleDanceStyleToggle(umbrella.id)}
                      color="orange"
                      styles={{
                        label: {
                          color: "#D0D0D0",
                          fontFamily: "'Space Grotesk', sans-serif",
                        },
                        input: {
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          borderColor: "rgba(255, 152, 0, 0.3)",
                          "&:checked": {
                            backgroundColor: "#FF9800",
                            borderColor: "#FF9800",
                          },
                        },
                      }}
                    />
                    {getSubStyles(umbrella.id).map((subStyle) => (
                      <Checkbox
                        key={subStyle.id}
                        label={subStyle.name}
                        checked={selectedDanceStyles.includes(subStyle.id)}
                        onChange={() => handleDanceStyleToggle(subStyle.id)}
                        color="orange"
                        ml="md"
                        styles={{
                          label: {
                            color: "#D0D0D0",
                            fontFamily: "'Space Grotesk', sans-serif",
                          },
                          input: {
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderColor: "rgba(255, 152, 0, 0.3)",
                            "&:checked": {
                              backgroundColor: "#FF9800",
                              borderColor: "#FF9800",
                            },
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}

              {/* Standalone Styles */}
              {getStandaloneStyles().length > 0 && (
                <Box>
                  <Text
                    size="md"
                    fw={600}
                    c="#FF9800"
                    mb="xs"
                    style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                  >
                    Other Styles
                  </Text>
                  <Stack gap="xs" ml="md">
                    {getStandaloneStyles().map((style) => (
                      <Checkbox
                        key={style.id}
                        label={style.name}
                        checked={selectedDanceStyles.includes(style.id)}
                        onChange={() => handleDanceStyleToggle(style.id)}
                        color="orange"
                        styles={{
                          label: {
                            color: "#D0D0D0",
                            fontFamily: "'Space Grotesk', sans-serif",
                          },
                          input: {
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderColor: "rgba(255, 152, 0, 0.3)",
                            "&:checked": {
                              backgroundColor: "#FF9800",
                              borderColor: "#FF9800",
                            },
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            <Divider color="rgba(255, 152, 0, 0.3)" />

            {/* Categories Section */}
            <Box>
              <Title
                order={4}
                c="#F0F0F0"
                mb="md"
                style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 600 }}
              >
                Categories
              </Title>
              <Stack gap="xs">
                {categories.map((category) => (
                  <Checkbox
                    key={category}
                    label={category}
                    checked={selectedCategories.includes(category)}
                    onChange={() => handleCategoryToggle(category)}
                    color="orange"
                    styles={{
                      label: {
                        color: "#D0D0D0",
                        fontFamily: "'Space Grotesk', sans-serif",
                      },
                      input: {
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        borderColor: "rgba(255, 152, 0, 0.3)",
                        "&:checked": {
                          backgroundColor: "#FF9800",
                          borderColor: "#FF9800",
                        },
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </ScrollArea>

        {/* Fixed Footer with Buttons */}
        <Box
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "16px",
            background: "rgba(23, 23, 23, 0.7)",
            backdropFilter: "blur(10px)",
            borderTop: "1px solid rgba(255, 152, 0, 0.2)",
          }}
        >
          <Group gap="8px" grow>
            <Button
              variant="light"
              size="sm"
              onClick={clearAllFilters}
              style={{
                borderColor: "#FF9800",
                color: "#FF9800",
                fontFamily: "'Big Shoulders Display', sans-serif",
                fontWeight: 600,
                backgroundColor: "transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#FF9800";
                e.currentTarget.style.color = "#171717";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#FF9800";
              }}
            >
              Clear All
            </Button>
            <Button
              variant="filled"
              size="sm"
              onClick={() => {
                setCurrentPage(1);
                toggleFilterDrawer();
              }}
              style={{
                backgroundColor: "#FF9800",
                color: "#171717",
                fontFamily: "'Big Shoulders Display', sans-serif",
                fontWeight: 600,
              }}
            >
              Show {filteredTerms.length}{" "}
              {filteredTerms.length === 1 ? "Word" : "Words"}
            </Button>
          </Group>
        </Box>
      </Drawer>

      {/* Term Details Drawer */}
      <Drawer
        opened={termDrawerOpened}
        onClose={toggleTermDrawer}
        title={
          selectedTerm && (
            <Group justify="space-between" style={{ width: "100%" }}>
              <Title
                order={3}
                style={{ fontFamily: "'Big Shoulders Display', sans-serif", color: "#F0F0F0" }}
              >
                {selectedTerm.name}
              </Title>
              <Button
                variant="outline"
                size="sm"
                color="orange"
                leftSection={<IconExternalLink size={16} />}
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                onClick={() => {
                  const slug = getWordSlug(selectedTerm.name);
                  window.location.href = `/dictionary/${slug}`;
                  toggleTermDrawer();
                }}
              >
                View
              </Button>
            </Group>
          )
        }
        position="right"
        size="lg"
        styles={{
          content: {
            background: "rgba(23, 23, 23, 0.95)",
            backdropFilter: "blur(15px)",
            border: "1px solid rgba(255, 152, 0, 0.3)",
          },
          header: {
            backgroundColor: "transparent",
            borderBottom: "1px solid rgba(255, 152, 0, 0.3)",
          },
          title: {
            fontFamily: "'Big Shoulders Display', sans-serif",
            color: "#F0F0F0",
          },
        }}
      >
        {selectedTerm && (
          <ScrollArea style={{ height: "100vh" }}>
            <Stack gap="lg" p="md">
              {/* Definition */}
              <Box>
                <Group gap="sm" mb="md">
                  {selectedTerm.danceStyles.map((styleId) => (
                    <L2KBadge
                      key={styleId}
                      variant="subtle"
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                      }}
                    >
                      {getDanceStyleName(styleId)}
                    </L2KBadge>
                  ))}
                  {/* Category Icons instead of badge */}
                  <Group gap="4px">
                    {getCategoryIcons(selectedTerm).map(
                      (IconComponent, index) => (
                        <Box
                          key={index}
                          style={{
                            color: "#FF9800",
                            opacity: 0.7,
                          }}
                        >
                          <IconComponent size={20} />
                        </Box>
                      )
                    )}
                  </Group>
                </Group>
                <Text
                  size="md"
                  c="#F0F0F0"
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    lineHeight: 1.6,
                  }}
                >
                  {selectedTerm.definition}
                </Text>
              </Box>

              {/* Internal Videos */}
              {selectedTerm.multimedia.some(
                (id) =>
                  multimediaReferences.find((ref) => ref.id === id)?.type ===
                  "internal_video"
              ) && (
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    mb="md"
                    style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                  >
                    Videos
                  </Title>
                  <Stack gap="sm">
                    {selectedTerm.multimedia
                      .map((id) =>
                        multimediaReferences.find((ref) => ref.id === id)
                      )
                      .filter((ref) => ref?.type === "internal_video")
                      .map((ref) => (
                        <Card
                          key={ref?.id}
                          padding="md"
                          style={{ background: "rgba(255, 255, 255, 0.05)" }}
                        >
                          <Text
                            c="#F0F0F0"
                            style={{
                              fontFamily: "'Space Grotesk', sans-serif",
                            }}
                          >
                            📹 {ref?.title}
                          </Text>
                        </Card>
                      ))}
                  </Stack>
                </Box>
              )}

              {/* Internal Images */}
              {selectedTerm.multimedia.some(
                (id) =>
                  multimediaReferences.find((ref) => ref.id === id)?.type ===
                  "internal_image"
              ) && (
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    mb="md"
                    style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                  >
                    Images
                  </Title>
                  <Stack gap="sm">
                    {selectedTerm.multimedia
                      .map((id) =>
                        multimediaReferences.find((ref) => ref.id === id)
                      )
                      .filter((ref) => ref?.type === "internal_image")
                      .map((ref) => (
                        <Card
                          key={ref?.id}
                          padding="md"
                          style={{ background: "rgba(255, 255, 255, 0.05)" }}
                        >
                          <Text
                            c="#F0F0F0"
                            style={{
                              fontFamily: "'Space Grotesk', sans-serif",
                            }}
                          >
                            🖼️ {ref?.title}
                          </Text>
                        </Card>
                      ))}
                  </Stack>
                </Box>
              )}

              {/* External Videos */}
              {selectedTerm.multimedia.some(
                (id) =>
                  multimediaReferences.find((ref) => ref.id === id)?.type ===
                  "external_video"
              ) && (
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    mb="md"
                    style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                  >
                    External Videos
                  </Title>
                  <Stack gap="sm">
                    {selectedTerm.multimedia
                      .map((id) =>
                        multimediaReferences.find((ref) => ref.id === id)
                      )
                      .filter((ref) => ref?.type === "external_video")
                      .map((ref) => (
                        <Card
                          key={ref?.id}
                          padding="md"
                          style={{ background: "rgba(255, 255, 255, 0.05)" }}
                        >
                          <Group justify="space-between">
                            <Text
                              c="#F0F0F0"
                              style={{
                                fontFamily: "'Space Grotesk', sans-serif",
                              }}
                            >
                              🎬 {ref?.title}
                            </Text>
                            <ActionIcon variant="subtle" color="orange">
                              <IconExternalLink size={16} />
                            </ActionIcon>
                          </Group>
                        </Card>
                      ))}
                  </Stack>
                </Box>
              )}

              {/* External Links */}
              {selectedTerm.multimedia.some(
                (id) =>
                  multimediaReferences.find((ref) => ref.id === id)?.type ===
                  "external_link"
              ) && (
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    mb="md"
                    style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                  >
                    External Links
                  </Title>
                  <Stack gap="sm">
                    {selectedTerm.multimedia
                      .map((id) =>
                        multimediaReferences.find((ref) => ref.id === id)
                      )
                      .filter((ref) => ref?.type === "external_link")
                      .map((ref) => (
                        <Card
                          key={ref?.id}
                          padding="md"
                          style={{ background: "rgba(255, 255, 255, 0.05)" }}
                        >
                          <Group justify="space-between">
                            <Text
                              c="#F0F0F0"
                              style={{
                                fontFamily: "'Space Grotesk', sans-serif",
                              }}
                            >
                              🔗 {ref?.title}
                            </Text>
                            <ActionIcon variant="subtle" color="orange">
                              <IconExternalLink size={16} />
                            </ActionIcon>
                          </Group>
                        </Card>
                      ))}
                  </Stack>
                </Box>
              )}

              {/* Podcasts */}
              {selectedTerm.multimedia.some(
                (id) =>
                  multimediaReferences.find((ref) => ref.id === id)?.type ===
                  "podcast"
              ) && (
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    mb="md"
                    style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                  >
                    Podcasts
                  </Title>
                  <Stack gap="sm">
                    {selectedTerm.multimedia
                      .map((id) =>
                        multimediaReferences.find((ref) => ref.id === id)
                      )
                      .filter((ref) => ref?.type === "podcast")
                      .map((ref) => (
                        <Card
                          key={ref?.id}
                          padding="md"
                          style={{ background: "rgba(255, 255, 255, 0.05)" }}
                        >
                          <Group justify="space-between">
                            <Text
                              c="#F0F0F0"
                              style={{
                                fontFamily: "'Space Grotesk', sans-serif",
                              }}
                            >
                              🎧 {ref?.title}
                            </Text>
                            <ActionIcon variant="subtle" color="orange">
                              <IconExternalLink size={16} />
                            </ActionIcon>
                          </Group>
                        </Card>
                      ))}
                  </Stack>
                </Box>
              )}

              {/* See Also */}
              {selectedTerm.seeAlso.length > 0 && (
                <Box>
                  <Title
                    order={4}
                    c="#F0F0F0"
                    mb="md"
                    style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
                  >
                    See Also
                  </Title>
                  <Group gap="sm">
                    {selectedTerm.seeAlso
                      .map((id) =>
                        dictionaryTerms.find((term) => term.id === id)
                      )
                      .filter(Boolean)
                      .map((relatedTerm) =>
                        relatedTerm ? (
                          <Button
                            key={relatedTerm.id}
                            variant="outline"
                            color="orange"
                            size="sm"
                            onClick={() => {
                              setSelectedTerm(relatedTerm);
                            }}
                            style={{
                              fontFamily: "'Space Grotesk', sans-serif",
                            }}
                          >
                            {relatedTerm.name}
                          </Button>
                        ) : null
                      )}
                  </Group>
                </Box>
              )}
            </Stack>
          </ScrollArea>
        )}
      </Drawer>
    </Box>
  );
}

// Ensure hideOnScroll is always true for DanceDictionary
const DanceDictionaryLayoutWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  console.log(
    "[DanceDictionaryLayoutWrapper] Rendering with hideOnScroll=true"
  );
  return <HybridLayout hideOnScroll={true}>{children}</HybridLayout>;
};

DanceDictionary.layout = (page: React.ReactNode) => {
  console.log(
    "[DanceDictionary.layout] Creating layout with hideOnScroll=true"
  );
  console.trace("[DanceDictionary.layout] Stack trace");
  return <DanceDictionaryLayoutWrapper>{page}</DanceDictionaryLayoutWrapper>;
};
